import style from "./style.module.scss";
import { Box, Container, Grid, Button, Menu, MenuItem, MenuProps, Typography } from "@mui/material";
import { Link, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../store";
import { checkEmptyObj, checkIf, getInitials, invalidateUserCache } from "../../utils";
import { ChevronIcon, EarthIcon, LogoutIcon, ProfileIcon } from "../../assets/icons";
import { resetSelection } from "../../store/selectionSlice";
import { set } from "../../store/globalSlice";
import useLogin from "../Auth/useLogin";
import React, { useEffect, useMemo, useState } from "react";
import { alpha, styled } from "@mui/material/styles";
import { getTenant } from "../../theme";
import useMembershipUsers from "../../hooks/useMembershipUsers";
import { useTranslation } from "react-i18next";
import { useLanguage } from "../../hooks/useLanguage";

const StyledMenu = styled((props: MenuProps) => (
  <Menu
    elevation={0}
    anchorOrigin={{
      vertical: "bottom",
      horizontal: "left",
    }}
    transformOrigin={{
      vertical: "top",
      horizontal: "left",
    }}
    {...props}
  />
))(({ theme }) => ({
  "& .MuiPaper-root": {
    borderRadius: 12,
    marginTop: theme.spacing(1),
    minWidth: 250,
    color: theme.palette.mode === "light" ? "rgb(55, 65, 81)" : theme.palette.grey[300],
    boxShadow: "0px 12px 32px 0px rgba(0, 0, 0, 0.25)",
    "& .MuiMenu-list": {
      padding: "16px",
      "& > a": {
        textDecoration: "none",
        color: theme.palette.text.fontDark,
        "&:not(:last-child) > li": {
          borderBottomWidth: "1px",
          borderBottomColor: theme.palette.text.base200,
          borderBottomStyle: "solid",
        },
      },
    },
    "& .MuiMenuItem-root": {
      "&": {
        padding: "12px",
        fontSize: "14px",
        fontWeight: 500,
        lineHeight: "normal",
      },
    },
  },
}));

interface LanguageSelectorButtonProps {
  language: string;
  languageMenuOpen: boolean;
  onLanguageMenuClick: (event: React.MouseEvent<HTMLElement>) => void;
  className?: string;
}

const LanguageSelectorButton: React.FC<LanguageSelectorButtonProps> = ({
  language,
  languageMenuOpen,
  onLanguageMenuClick,
  className,
}) => {
  return (
    <Button
      onClick={onLanguageMenuClick}
      sx={{
        backgroundColor: "text.base100",
        padding: "12px 16px",
        borderRadius: "90px",
        color: "text.fontDark",
        fontSize: "14px",
        margin: "0 0 0 8px",
        fontWeight: 600,
        lineHeight: "22px",
        minWidth: "auto",
        display: "flex",
        alignItems: "center",
        gap: "6px",
        textTransform: "none",
        "&:hover": {
          backgroundColor: "text.base200",
        },
        "@media (max-width: 1200px)": {
          padding: "10px 12px",
          fontSize: "13px",
          margin: "0 0 0 4px",
          gap: "4px",
        },
      }}
      className={className}
    >
      <Box
        sx={{
          width: "20px",
          height: "20px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          "@media (max-width: 1200px)": {
            width: "18px",
            height: "18px",
          },
        }}
      >
        <EarthIcon />
      </Box>
      <Box
        component="span"
        sx={{
          fontSize: "14px",
          "@media (max-width: 1200px)": {
            fontSize: "13px",
          },
        }}
      >
        {language === "ar" ? "ع" : "EN"}
      </Box>
      <Typography
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          transform: languageMenuOpen ? "rotate(180deg)" : "rotate(0deg)",
          transition: "transform 0.2s ease-in-out",
        }}
        component="span"
      >
        <ChevronIcon />
      </Typography>
    </Button>
  );
};

export default function Header() {
  const { t } = useTranslation("header");
  const { language, changeLanguage } = useLanguage();
  const user = useSelector((state: RootState) => state.user);
  const [searchParams] = useSearchParams();
  const invitationCode = searchParams.get("code");
  const dispatch = useDispatch();
  const { logout } = useLogin();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [languageAnchorEl, setLanguageAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const languageMenuOpen = Boolean(languageAnchorEl);
  const tenant = getTenant();
  const [selectedUser, setSelectedUser] = useState<string>();

  const appUserId = localStorage.getItem("app-user-id");
  const [retriggerMembershipUsers, setRetriggerMembershipUsers] = useState<number>();
  const { users: membershipUsers, loading: membershipUsersLoading } = useMembershipUsers(retriggerMembershipUsers);

  const memberShipUsersList = useMemo(() => {
    if (!membershipUsers) return [];

    if (appUserId) {
      return membershipUsers.users?.filter((membershipUser) => membershipUser.id !== appUserId);
    } else {
      return membershipUsers.users?.filter((membershipUser) => membershipUser.mainUser !== true);
    }
  }, [appUserId, membershipUsers]);

  const noExistingUsers = useMemo(() => {
    return !(memberShipUsersList?.length && memberShipUsersList?.length > 0);
  }, [memberShipUsersList]);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLanguageClose = () => {
    setLanguageAnchorEl(null);
  };

  useEffect(() => {
    if (selectedUser) {
      localStorage.setItem("app-user-id", selectedUser);
      invalidateUserCache();
      window.location.reload();
    }
  }, [selectedUser]);

  // Ensure menu is closed when component mounts or user state changes
  useEffect(() => {
    setAnchorEl(null);
  }, [user]);

  return (
    <Box className={style.header_wrapper} sx={{ width: 1 }}>
      <Container className={style.header} maxWidth="lg">
        <Grid
          item
          xs={12}
          sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", height: "80px" }}
        >
          <div className={style.logo_container}>
            <Link to={`/`}>
              <Box
                sx={{
                  backgroundImage: `url(../../${tenant.name}/logoHorizontal.svg)`,
                  height: `${tenant.logo.height}px`,
                  width: `${tenant.logo.width}px`,
                  backgroundRepeat: "no-repeat",
                  backgroundSize: "contain",
                }}
              />
            </Link>
          </div>
          <div className={style.menu_container}>
            <div className={style.inner_menu}>
              <ul>
                <li>
                  {tenant.name === "rejeem" ? (
                    <Link to={checkEmptyObj(user) ? `/all-plans` : `/recommended-plans`}>{t("plans")}</Link>
                  ) : (
                    <Link to={`/plans`}>{t("plans")}</Link>
                  )}
                </li>
                <li>
                  <Link to={`/dietitian-appointment`}>{t("dietitianAppointment")}</Link>
                </li>
              </ul>
            </div>

            {checkEmptyObj(user) ? (
              <div className={style.buttons}>
                {tenant.name === "rejeem" && (
                  <Link to={"/partner-application"}>
                    <Button
                      sx={{
                        backgroundColor: "text.base100",
                        padding: "12px 24px",
                        borderRadius: "90px",
                        color: "text.fontDark",
                        fontSize: "14px",
                        margin: "0 8px",
                        fontWeight: 600,
                        lineHeight: "22px",
                      }}
                      className={style.menu_buttons}
                    >
                      {t("becomePartner")}
                    </Button>
                  </Link>
                )}
                {tenant.name === "rejeem" ? (
                  <Button
                    onClick={() => {
                      dispatch(set({ showLoginModal: true }));
                    }}
                    className={style.menu_buttons}
                    color="primary"
                    variant="outlined"
                    disableElevation
                  >
                    {t("login")}
                  </Button>
                ) : (
                  <Link to={`/`}>
                    <Button className={style.menu_buttons} color="primary" variant="outlined" disableElevation>
                      {t("login")}
                    </Button>
                  </Link>
                )}

                <Link to={`${checkIf(invitationCode) ? "/quick-register?code=" + invitationCode : "/join"}`}>
                  <Button
                    className={style.menu_buttons}
                    color="primary"
                    variant="contained"
                    disableElevation
                    onClick={() => dispatch(resetSelection())}
                  >
                    {t("joinNow")}
                  </Button>
                </Link>

                <LanguageSelectorButton
                  language={language}
                  languageMenuOpen={languageMenuOpen}
                  onLanguageMenuClick={(e) => setLanguageAnchorEl(e.currentTarget)}
                  className={style.menu_buttons}
                />
              </div>
            ) : (
              <div className={style.menu_wrapper}>
                <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                  <div className={style.userMenu} onClick={handleClick}>
                    <div className={style.icon}>{getInitials(String(user.fullName))}</div>
                    <div className={style.name}>{user.fullName}</div>
                    <Typography
                      sx={{
                        display: "flex",
                        color: "text.fontLight",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                      component="span"
                    >
                      <ChevronIcon />
                    </Typography>
                  </div>
                  <StyledMenu anchorEl={anchorEl} open={open} onClose={handleClose}>
                    {memberShipUsersList?.length && memberShipUsersList?.length > 0 ? (
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          borderRadius: "12px",
                          padding: "0 12px",
                          border: "1px solid var(--base-100)",
                          marginBottom: "10px",
                        }}
                      >
                        {memberShipUsersList?.map((user) => (
                          <Box
                            key={user.id}
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              padding: "12px 0",
                              cursor: "pointer",
                              gap: "4px",
                              "&:not(:last-child)": {
                                borderBottom: "1px solid var(--base-100)",
                              },
                            }}
                            onClick={() => {
                              setSelectedUser(user.id);
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                gap: "8px",
                                alignItems: "center",
                              }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  flexDirection: "row",
                                  alignItems: "center",
                                  gap: "8px",
                                  borderRadius: "8px",
                                  backgroundColor: "var(--base-50)",
                                  width: "32px",
                                  height: "32px",
                                  justifyContent: "center",
                                }}
                              >
                                <Typography
                                  sx={{
                                    color: "text.fontDefault",
                                    fontSize: "14px",
                                    fontWeight: "500",
                                    lineHeight: "22px",
                                  }}
                                >
                                  {getInitials(String(user.fullName))}
                                </Typography>
                              </Box>

                              <Typography
                                sx={{
                                  color: "text.fontDarker",
                                  fontSize: "14px",
                                  fontWeight: "500",
                                  lineHeight: "22px",
                                }}
                              >
                                {user.fullName}
                              </Typography>
                            </Box>
                            {user.mainUser && (
                              <Typography
                                sx={{
                                  color: "text.successDark",
                                  borderRadius: "8px",
                                  padding: "4px 8px",
                                  backgroundColor: "var(--success-lighter)",
                                  fontSize: "12px",
                                  fontWeight: "600",
                                  lineHeight: "150%",
                                }}
                              >
                                {t("primary")}
                              </Typography>
                            )}
                          </Box>
                        ))}
                      </Box>
                    ) : null}

                    <Link to={`/profile/info`}>
                      <MenuItem onClick={handleClose}>{t("profile")}</MenuItem>
                    </Link>
                    <Link to={`/meal-plan`}>
                      <MenuItem onClick={handleClose}>{t("mealPlan")}</MenuItem>
                    </Link>
                    <Link to={`/profile/plan-info`}>
                      <MenuItem onClick={handleClose}>{t("subscriptions")}</MenuItem>
                    </Link>
                    <Link to={`/dietitian-appointment`}>
                      <MenuItem onClick={handleClose}>{t("dietitianAppointment")}</MenuItem>
                    </Link>

                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        cursor: "pointer",
                        padding: "12px 12px 0 12px",
                      }}
                      onClick={logout}
                    >
                      <Typography
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: "text.errorDefault",
                          backgroundColor: "var(--base-50)",
                          borderRadius: "8px",
                          width: "28px",
                          height: "28px",
                        }}
                      >
                        <LogoutIcon />
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "14px",
                          fontWeight: 500,
                          lineHeight: "normal",
                          color: "var(--font-dark)",
                        }}
                      >
                        {t("logout")}
                      </Typography>
                    </Box>
                  </StyledMenu>

                  <LanguageSelectorButton
                    language={language}
                    languageMenuOpen={languageMenuOpen}
                    onLanguageMenuClick={(e) => setLanguageAnchorEl(e.currentTarget)}
                  />
                </div>
              </div>
            )}

            {/* Language Menu - Shared across both logged-in and anonymous states */}
            <Menu
              anchorEl={languageAnchorEl}
              open={languageMenuOpen}
              onClose={handleLanguageClose}
              elevation={0}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "right",
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
              sx={{
                "& .MuiPaper-root": {
                  borderRadius: "12px",
                  marginTop: 1,
                  minWidth: "180px",
                  boxShadow: "0px 12px 32px 0px rgba(0, 0, 0, 0.25)",
                },
                "& .MuiMenu-list": {
                  padding: "16px",
                },
              }}
            >
              <MenuItem
                onClick={() => {
                  changeLanguage("en");
                  handleLanguageClose();
                }}
                sx={{
                  backgroundColor: language === "en" ? "var(--base-50)" : "transparent",
                  fontWeight: language === "en" ? 600 : 500,
                  borderRadius: "8px",
                  marginBottom: "4px",
                  "&:hover": {
                    backgroundColor: language === "en" ? "var(--base-100)" : "var(--base-50)",
                  },
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: "12px", width: "100%" }}>
                  <Box component="span">{t("english")}</Box>
                </Box>
              </MenuItem>
              <MenuItem
                onClick={() => {
                  changeLanguage("ar");
                  handleLanguageClose();
                }}
                sx={{
                  backgroundColor: language === "ar" ? "var(--base-50)" : "transparent",
                  fontWeight: language === "ar" ? 600 : 500,
                  borderRadius: "8px",
                  "&:hover": {
                    backgroundColor: language === "ar" ? "var(--base-100)" : "var(--base-50)",
                  },
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: "12px", width: "100%" }}>
                  <Box component="span">{t("arabic")}</Box>
                </Box>
              </MenuItem>
            </Menu>
          </div>
        </Grid>
      </Container>
    </Box>
  );
}
